import React, { useEffect, useState } from 'react';
import { Alert, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getWallet } from '../../services/usage.service';

export interface StreamNotice { code: string; message: string; used_pct?: number }

const key = (state: string) => `wallet-banner-dismissed:${state}`;

const readDismissed = (state: string): boolean => {
  try { return sessionStorage.getItem(key(state)) === '1'; } catch { return false; }
};
const writeDismissed = (state: string) => {
  try { sessionStorage.setItem(key(state), '1'); } catch { /* ignore */ }
};

export const WalletBanner: React.FC<{ notice: StreamNotice | null }> = ({ notice }) => {
  const { data: wallet } = useQuery({
    queryKey: ['usage', 'wallet'], queryFn: getWallet, staleTime: 60_000, refetchInterval: 120_000,
  });
  const [, force] = useState(0);

  // Resolve what to show: a live stream notice wins, then the wallet query.
  let state: string | null = null;
  let severity: 'warning' | 'error' | 'info' = 'warning';
  let text = '';
  if (notice && notice.code.startsWith('balance_')) {
    state = notice.code;
    severity = notice.code === 'balance_exhausted' ? 'error' : notice.code === 'balance_unavailable' ? 'info' : 'warning';
    text = notice.message;
  } else if (wallet?.unavailable) {
    state = 'unavailable'; severity = 'info';
    text = 'AI balance could not be checked right now; the agent keeps working.';
  } else if (wallet?.state === 'exhausted') {
    state = 'exhausted'; severity = 'error';
    text = "Your organization's AI balance is exhausted. New sessions are blocked until balance is added.";
  } else if (wallet?.state === 'warning') {
    state = 'warning'; severity = 'warning';
    text = `Your organization has used ${wallet.used_pct ?? 80}% of its AI balance.`;
  }

  useEffect(() => { force((n) => n + 1); }, [state]);

  if (!state || readDismissed(state)) return null;

  return (
    <Alert severity={severity} onClose={() => { writeDismissed(state!); force((n) => n + 1); }} sx={{ borderRadius: 0 }}>
      {text}{' '}
      <Link component={RouterLink} to="/usage" underline="always">Usage</Link>
    </Alert>
  );
};

export default WalletBanner;
